import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";

interface NetworkStatusContextType {
  isOnline: boolean;
}

const NetworkStatusContext = createContext<NetworkStatusContextType>({ isOnline: true });

export function NetworkStatusProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowReconnected(true);
      setTimeout(() => setShowReconnected(false), 3000);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <NetworkStatusContext.Provider value={{ isOnline }}>
      {/* Offline banner */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-destructive text-destructive-foreground text-center py-2.5 px-4 text-sm font-medium shadow-lg">
          ⚠️ You're not connected to the internet. Some features may not work until your connection is restored.
        </div>
      )}

      {/* Reconnected banner */}
      {showReconnected && isOnline && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] text-center py-2.5 px-4 text-sm font-medium shadow-lg transition-opacity">
          ✅ You're back online!
        </div>
      )}

      {/* Push content down when banner is showing */}
      <div className={!isOnline || showReconnected ? "pt-10" : ""}>
        {children}
      </div>
    </NetworkStatusContext.Provider>
  );
}

export const useNetworkStatus = () => useContext(NetworkStatusContext);
